import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LoadingState, ErrorState } from "@/components/common/States";
import { StatusBadge } from "@/components/common/StatusBadge";
import { label } from "@/lib/labels";
import { fmtDate, fmtShort } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ChevronRight, MapPin, Navigation, Truck, Wrench, AlertTriangle, CalendarDays, CheckCircle2, ClipboardList, Mic, Play, ArrowRight } from "lucide-react";
import "@/components/field/field.css";

export const mapsUrl = (address, lat, lng) => (lat && lng ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}` : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address || "")}`);

const progress = (t) => { const c = t.checklist || []; return c.length ? [c.filter((x) => x.done).length, c.length] : null; };

export const TaskCard = ({ t }) => {
  const p = progress(t);
  return (
    <Link to={`/felt/oppgave/${t.id}`} className={cn("field-row", t.overdue && "border-rose-200 dark:border-rose-900/60")} data-testid={`field-task-card-${t.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[15px] font-medium leading-snug">{t.title}</div>
          <div className="mt-0.5 truncate text-xs text-muted-foreground">{t.project_name || "Uten prosjekt"} · {label(t.type)}</div>
        </div>
        <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <StatusBadge status={t.status} />
        {t.priority && t.priority !== "normal" && <StatusBadge status={t.priority} />}
        {t.due_date && <span className={cn("text-[11px] tabular", t.overdue ? "font-medium text-rose-700 dark:text-rose-300" : "text-muted-foreground")}>{t.overdue ? "Forfalt " : "Frist "}{fmtShort(t.due_date)}</span>}
        {p && <span className="ml-auto text-[11px] text-muted-foreground tabular">{p[0]}/{p[1]} punkter</span>}
      </div>
    </Link>
  );
};

const Section = ({ icon: Icon, title, count, to, testId }) => (
  <div className="field-sec" data-testid={testId}>
    <h2 className="flex items-center gap-2 text-[13.5px] font-semibold"><Icon className="h-4 w-4 text-primary" /> {title} {count !== undefined && <span className="font-normal text-muted-foreground tabular">({count})</span>}</h2>
    {to && <Link to={to} className="text-xs font-medium text-primary min-h-[32px] inline-flex items-center">Se alle</Link>}
  </div>
);

/** Hero: den oppgaven du mest sannsynlig skal ta nå – pågående først, ellers første med frist i dag. */
function NextTask({ task }) {
  if (!task) {
    return (
      <div className="field-card flex items-center gap-3 p-4" data-testid="field-today-empty">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><CheckCircle2 className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1"><div className="text-[15px] font-medium">Ingen oppgaver med frist i dag</div><div className="text-xs text-muted-foreground">Sjekk kommende oppgaver, eller registrer et avvik.</div></div>
        <Link to="/felt/oppgaver" className="text-primary" aria-label="Til oppgaver"><ArrowRight className="h-4 w-4" /></Link>
      </div>
    );
  }
  const p = progress(task);
  const running = task.status === "pagar";
  return (
    <Link to={`/felt/oppgave/${task.id}`} className="field-hero block" data-testid="field-next-task">
      <div className="relative flex items-center justify-between text-[11.5px] opacity-90">
        <span className="inline-flex items-center gap-1.5">{running ? <><i className="field-live" /> Pågår nå</> : <><Play className="h-3 w-3" /> Neste oppgave</>}</span>
        <span className="tabular">{task.due_date ? (task.overdue ? "Forfalt" : `Frist ${fmtShort(task.due_date)}`) : label(task.type)}</span>
      </div>
      <div className="relative mt-1.5 text-[16px] font-semibold leading-snug tracking-tight">{task.title}</div>
      <div className="relative mt-0.5 truncate text-xs opacity-85">{task.project_name || "Uten prosjekt"} · {label(task.type)}</div>
      <div className="relative mt-3 flex items-center gap-3">
        {p ? <><div className="field-bar"><i style={{ "--p": p[0] / p[1] }} /></div><span className="text-xs tabular opacity-90">{p[0]}/{p[1]}</span></> : <div className="flex-1 text-xs opacity-85">{task.description ? task.description.slice(0, 60) : "Trykk for å åpne oppgaven"}</div>}
        <span className="field-hero-btn">{running ? "Fortsett" : "Åpne"} <ChevronRight className="h-3.5 w-3.5" /></span>
      </div>
    </Link>
  );
}

export default function FieldHome() {
  const { user } = useAuth();
  const q = useQuery({ queryKey: ["field-today"], queryFn: () => get("/field/today"), refetchInterval: 60_000 });
  if (q.isLoading) return <LoadingState text="Henter dagens plan…" />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const d = q.data;
  const hour = new Date().getHours();
  const greet = hour < 10 ? "God morgen" : hour < 17 ? "Hei" : "God kveld";
  const todayTasks = d.tasks_today || [];
  const hero = todayTasks.find((t) => t.status === "pagar") || todayTasks[0] || null;
  const restToday = todayTasks.filter((t) => t.id !== hero?.id);
  const upcoming = (d.tasks || []).filter((t) => !todayTasks.some((x) => x.id === t.id)).slice(0, 5);
  const transports = (d.transports || []).slice(0, 3);
  const services = (d.service_jobs || []).slice(0, 3);
  const issues = d.issues || [];
  const running = todayTasks.filter((t) => t.status === "pagar").length;
  const overdue = todayTasks.filter((t) => t.overdue).length;

  return (
    <div className="space-y-3.5 fade-up" data-testid="field-home">
      <div>
        <div className="text-xs text-muted-foreground first-letter:uppercase">{fmtDate(d.today, "EEEE d. MMMM")}</div>
        <h1 className="text-[22px] font-display font-semibold tracking-tight">{greet}, {(user?.name || "").split(" ")[0]}</h1>
      </div>

      <NextTask task={hero} />

      <div className="grid grid-cols-3 gap-2">
        <Link to="/felt/oppgaver" className={cn("field-stat", overdue && "border-rose-200 dark:border-rose-900/60")} data-testid="field-kpi-tasks">
          <div className="text-[11px] text-muted-foreground">Oppgaver</div>
          <div className="text-[22px] font-display font-semibold tabular leading-tight">{todayTasks.length}</div>
          <div className={cn("text-[11px]", overdue ? "text-rose-700 dark:text-rose-300" : "text-muted-foreground")}>{overdue ? `${overdue} forfalt` : running ? `${running} pågår` : "i dag"}</div>
        </Link>
        <Link to="/felt/transport" className="field-stat" data-testid="field-kpi-transports">
          <div className="text-[11px] text-muted-foreground">Transport</div>
          <div className="text-[22px] font-display font-semibold tabular leading-tight">{(d.transports || []).length}</div>
          <div className="text-[11px] text-muted-foreground">neste 14 dager</div>
        </Link>
        <div className={cn("field-stat", issues.some((i) => i.severity === "kritisk") && "border-rose-200 dark:border-rose-900/60")} data-testid="field-kpi-issues">
          <div className="text-[11px] text-muted-foreground">Mine avvik</div>
          <div className="text-[22px] font-display font-semibold tabular leading-tight">{issues.length}</div>
          <div className="text-[11px] text-muted-foreground">åpne</div>
        </div>
      </div>

      <Button asChild variant="outline" className="h-12 w-full justify-start gap-3 rounded-2xl border-amber-300/70 bg-amber-50/60 px-3.5 text-amber-900 hover:bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
        <Link to="/felt/avvik/ny" data-testid="field-home-new-issue-button">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300"><Mic className="h-4 w-4" /></span>
          <span className="flex-1 text-left"><span className="block text-[14px] font-semibold leading-tight">Nytt avvik – snakk det inn</span><span className="block text-[11.5px] font-normal opacity-80">KI strukturerer, prosjektleder varsles</span></span>
          <AlertTriangle className="h-4 w-4 opacity-70" />
        </Link>
      </Button>

      {restToday.length > 0 && (<>
        <Section icon={CalendarDays} title="I dag" count={todayTasks.length} to="/felt/oppgaver" />
        <div className="space-y-2">{restToday.map((t) => <TaskCard key={t.id} t={t} />)}</div>
      </>)}

      {upcoming.length > 0 && (<>
        <Section icon={ClipboardList} title="Kommende" count={(d.tasks || []).length - todayTasks.length} to="/felt/oppgaver" />
        <div className="space-y-2">{upcoming.map((t) => <TaskCard key={t.id} t={t} />)}</div>
      </>)}

      <Section icon={Truck} title="Transport" count={(d.transports || []).length} to="/felt/transport" />
      {transports.length === 0 ? <div className="px-1 text-sm text-muted-foreground">Ingen transporter de neste dagene.</div> : (
        <div className="space-y-2">
          {transports.map((t) => (
            <Link key={t.id} to={`/felt/transport?id=${t.id}`} className="field-row" data-testid={`field-transport-card-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0"><div className="text-[15px] font-medium">{t.transport_number} · {label(t.direction)}</div><div className="truncate text-xs text-muted-foreground">{t.project_name || t.to_address}</div></div>
                <StatusBadge status={t.status} />
              </div>
              <div className="mt-1.5 flex items-center gap-3 text-[11px] text-muted-foreground"><span className="tabular">{fmtShort(t.date)}{t.time_window ? ` ${t.time_window}` : ""}</span><span>{(t.module_ids || []).length} moduler</span>{t.crane_required && <span>Kran</span>}</div>
            </Link>
          ))}
        </div>
      )}

      {services.length > 0 && (<>
        <Section icon={Wrench} title="Service" count={(d.service_jobs || []).length} />
        <div className="space-y-2">
          {services.map((s) => (
            <div key={s.id} className="field-row" data-testid={`field-service-card-${s.id}`}>
              <div className="flex items-start justify-between gap-2"><div className="min-w-0"><div className="text-[15px] font-medium">{s.module_code} · {label(s.type)}</div><div className="truncate text-xs text-muted-foreground">{s.description || s.title}</div></div><StatusBadge status={s.status} /></div>
              {s.due_date && <div className="mt-1.5 text-[11px] text-muted-foreground tabular">Frist {fmtShort(s.due_date)}</div>}
            </div>
          ))}
        </div>
      </>)}

      {issues.length > 0 && (<>
        <Section icon={AlertTriangle} title="Mine avvik" count={issues.length} />
        <div className="space-y-2">
          {issues.slice(0, 4).map((i) => (
            <div key={i.id} className="field-row" data-testid={`field-issue-card-${i.id}`}>
              <div className="flex items-start justify-between gap-2"><div className="min-w-0"><div className="text-[15px] font-medium">{i.title}</div><div className="text-xs text-muted-foreground">{i.issue_number} · {fmtShort(i.created_at)}</div></div><div className="flex gap-1"><StatusBadge status={i.severity} /><StatusBadge status={i.status} /></div></div>
            </div>
          ))}
        </div>
      </>)}

      <Section icon={MapPin} title="Mine prosjekter" count={(d.projects || []).length} />
      {(d.projects || []).length === 0 ? <div className="px-1 text-sm text-muted-foreground">Du er ikke knyttet til aktive prosjekter.</div> : (
        <div className="space-y-2">
          {d.projects.map((p) => (
            <div key={p.id} className="field-row flex items-center gap-3" data-testid={`field-project-card-${p.id}`}>
              <Link to={`/felt/prosjekt/${p.id}`} className="min-w-0 flex-1">
                <div className="truncate text-[15px] font-medium">{p.name}</div>
                <div className="truncate text-xs text-muted-foreground">{p.project_number} · {p.address || "Ukjent adresse"}</div>
                <div className="mt-1 flex items-center gap-2"><StatusBadge status={p.status} /><span className="text-[11px] text-muted-foreground tabular">{fmtShort(p.start_date)} – {fmtShort(p.end_date)}</span></div>
              </Link>
              <Button asChild size="icon" variant="outline" className="h-11 w-11 shrink-0 rounded-xl" aria-label="Naviger">
                <a href={mapsUrl(p.address, p.lat, p.lng)} target="_blank" rel="noreferrer" data-testid={`field-navigate-${p.id}`}><Navigation className="h-4 w-4" /></a>
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="pb-2 text-center text-[11px] text-muted-foreground">Oppdateres automatisk hvert minutt</div>
    </div>
  );
}
